import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient } from '../api';
import { Fachaktionen } from './Fachaktionen';
import { IconOk, IconEbene, IconSuche } from '../Icons';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte, type Zustand,
} from '../muster';

/**
 * Fremdmodelle > Fremdmodellaufnahme.
 *
 * Was sich in Architektur, Tragwerk, Küche und Medientechnik geändert hat.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIESE SEITE UMGEBAUT WURDE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vorher stand alles untereinander: Standauswahl, Folgen, dann je Geschoss
 * eine Karte, darin je Planungsbereich eine Tabelle. Bei 1.418 Änderungen war
 * das eine Seite, die man nur durchrollen konnte — und die Folgen, der
 * eigentliche Wert des Moduls, lagen zwischen Auswahl und Liste begraben.
 *
 * Jetzt derselbe Aufbau wie bei der Heizung: links die Gliederung
 * Geschoss → Planungsbereich, rechts die Änderungen des gewählten Bereichs.
 * Und die Folgen sind eine eigene Ansicht, keine Karte im Durchlauf.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE WOCHE IST DIE EINHEIT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Montags kommen die Modelle über ACC, und der Vergleich läuft gegen den
 * Vorwochenstand. Jede Wochenliste bleibt stehen: Änderungen werden oft erst
 * später eingearbeitet, und wer im Mai nachsehen will, was im März geändert
 * wurde, braucht die Märzliste — nicht die Summe seither.
 *
 * Deshalb ist der Stand ein Auswahlfeld und keine Ansicht auf „alles seit
 * Beginn".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER ERSTE LAUF HAT KEINEN VERGLEICH
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Er legt den Bezugsstand. Wer ihn als Vergleich rechnete, bekäme 50.000
 * Änderungen der Art „neu" — und danach glaubt niemand mehr einer
 * Änderungsliste.
 *
 * Und offen bleibt offen, auch über Wochen: Verschwände eine kritische
 * Änderung beim nächsten Vergleich, weil sie dann alt ist, wäre der Bericht
 * unzuverlässig.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

const RELEVANZ: Record<string, { zustand: Zustand; text: string; rang: number }> = {
  kritisch: { zustand: 'fehler', text: 'kritisch', rang: 0 },
  hoch: { zustand: 'warnung', text: 'hoch', rang: 1 },
  mittel: { zustand: 'warnung', text: 'mittel', rang: 2 },
  gering: { zustand: 'neutral', text: 'gering', rang: 3 },
};

const ART: Record<string, string> = {
  neu: 'neu im Modell',
  entfernt: 'entfernt',
  geaendert: 'geändert',
  ersetzt: 'ersetzt',
};

export function Fremdmodellaufnahme() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [staende, setStaende] = useState<any[] | null>(null);
  const [standId, setStandId] = useState<string>('');
  const [baum, setBaum] = useState<any | null>(null);
  const [nurOffen, setNurOffen] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<{ geschoss: string; bereich: string }
    | null>(null);
  const [ansicht, setAnsicht] = useState<'aenderungen' | 'folgen'>('aenderungen');

  const [folgen, setFolgen] = useState<any | null>(null);
  const [pruefend, setPruefend] = useState(false);
  const [pruefmeldung, setPruefmeldung] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    api.fremdStaende(projekt.id)
      .then((x) => { setStaende(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id]);

  const laden = () => {
    if (!projekt) return;

    api.fremdBaum(projekt.id, standId || undefined,
                  nurOffen ? 'offen' : undefined)
      .then(setBaum)
      .catch(() => setBaum(null));
  };

  useEffect(() => { laden(); }, [api, projekt?.id, standId, nurOffen]);

  const folgenLaden = () => {
    if (!projekt) return;
    api.fremdFolgen(projekt.id).then(setFolgen).catch(() => setFolgen(null));
  };

  useEffect(() => { folgenLaden(); }, [api, projekt?.id]);

  /**
   * Die Folgen prüfen — ein eigener Knopf und kein Automatismus.
   *
   * Die Prüfung läuft über 17.939 Hosts und ist die teuerste Rechnung des
   * Moduls. Sie bei jedem Lesen mitlaufen zu lassen hieße, sie auch dann zu
   * rechnen, wenn niemand hinsieht.
   */
  const folgenPruefen = async () => {
    if (!projekt) return;

    setPruefend(true);
    setPruefmeldung(null);

    try {
      const e = await api.fremdFolgenPruefen(projekt.id, standId || undefined);

      setPruefmeldung(
        `${z(e.betrachtet ?? 0)} Änderungen geprüft, ${z(e.folgen ?? 0)} `
        + `Folge(n) mit ${z(e.betroffeneBauteile ?? 0)} betroffenen eigenen `
        + 'Bauteilen.');

      folgenLaden();
    } catch (e) {
      setPruefmeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setPruefend(false);
    }
  };

  // Die Suche filtert im Browser — der Baum trägt nur Struktur und Zahlen.
  const gefiltert = useMemo(() => {
    if (!baum?.geschosse) return null;
    if (!suche.trim()) return baum.geschosse;

    const t = suche.trim().toLowerCase();
    const passt = (s?: string | null) => (s ?? '').toLowerCase().includes(t);

    return baum.geschosse
      .map((g: any) => ({
        ...g,
        bereiche: (g.bereiche ?? []).filter(
          (b: any) => passt(b.planungsbereich)
            || (b.aenderungen ?? []).some(
              (a: any) => passt(a.typname) || passt(a.kategorie)),
        ),
      }))
      .filter((g: any) => passt(g.geschoss) || g.bereiche.length > 0);
  }, [baum, suche]);

  if (!projekt) {
    return <LoadingState text="Projekt wird geladen …" zeilen={5} />;
  }

  const leer = !staende || staende.length === 0;

  const gewaehlterBereich = gewaehlt && baum?.geschosse
    ? baum.geschosse.find((g: any) => g.geschoss === gewaehlt.geschoss)
        ?.bereiche.find((b: any) => b.planungsbereich === gewaehlt.bereich)
    : null;

  return (
    <>
      {/* Die Auftragsart 120.
          ══════════════════════════════════════════════════════════════
          WARUM EINE EIGENE ROLLE UND NICHT DIE DER AUSSPARUNGEN

          Der Lauf liest die VERKNÜPFUNGEN auf Architektur, Tragwerk, Küche
          und Medientechnik. Die hängen am Koordinationsmodell
          TP1_TUV_MX_MEP_TGAA_002, nicht am Aussparungsmodell.

          Mit der Aussparungsrolle lief er im falschen Modell und fand dort
          die Fremdmodelle gar nicht — ein Lauf ohne Befund, der aussieht
          wie „nichts geändert". */}
      <Fachaktionen
        api={api}
        rolleKey="fremdmodelle"
        leseArt={120}
        leseText={leer ? 'Erstaufnahme starten' : 'Wochenstand lesen'}
      />

      {fehler && (
        <ErrorState
          meldung="Die Fremdmodellaufnahme konnte nicht geladen werden."
          technisch={fehler}
        />
      )}

      {leer ? (
        <EmptyState
          titel="Noch kein Fremdmodellstand."
          erklaerung={'Der erste Lauf ist die Erstaufnahme: Er legt den Bezugsstand und '
            + 'hat keinen Vergleich. Ab dem zweiten Lauf wird gegen den Vorwochenstand '
            + 'verglichen. Gelesen werden Architektur, Tragwerk, Küche und '
            + 'Medientechnik. Gespeichert wird nicht das Bauteil, sondern sein '
            + 'Fingerabdruck — auf 20 mm gerundet, damit Scheinänderungen wegfallen.'}
        />
      ) : (
        <>
          {/* Die Kennzahlen zuerst — kritisch und offen vor der Gesamtzahl. */}
          {baum && (
            <KpiReihe>
              <KpiCard
                titel="Kritisch" wert={baum.kritisch}
                vergleich="lösen eine Nacharbeit aus"
                zustand={baum.kritisch > 0 ? 'fehler' : undefined}
                zustandText={baum.kritisch > 0 ? 'zuerst ansehen' : undefined}
              />
              <KpiCard titel="Offen" wert={baum.offen} />
              <KpiCard titel="Änderungen im Stand" wert={baum.gesamt} />
              <KpiCard
                titel="Folgen im eigenen Bestand" wert={folgen?.gesamt ?? 0}
                fussnote="Die Kette Bauteil → Aussparung → Brandschott."
                zustand={(folgen?.gesamt ?? 0) > 0 ? 'warnung' : undefined}
                zustandText={(folgen?.gesamt ?? 0) > 0 ? 'nacharbeiten' : undefined}
              />
            </KpiReihe>
          )}

          {/* Der Stand: eine Zeile, nicht eine Karte. */}
          <div className="karte" style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                          flexWrap: 'wrap' }}>
              <label className="schwach" style={{ fontSize: 12 }}>Stand</label>

              <select value={standId}
                      onChange={(e) => setStandId(e.target.value)}>
                <option value="">neuester Vergleich</option>
                {staende!.filter((s) => !s.istBezugsstand).map((s) => (
                  <option key={s.id} value={s.id}>
                    {new Date(s.gelesenAm).toLocaleDateString('de-DE')} ·{' '}
                    {s.verknuepfung} · {z(s.aenderungen)} Änderungen
                    {s.offen > 0 ? ` (${z(s.offen)} offen)` : ''}
                  </option>
                ))}
              </select>

              <label style={{ display: 'flex', gap: 6, alignItems: 'center',
                              fontSize: 13 }}>
                <input type="checkbox" checked={nurOffen}
                       onChange={(e) => setNurOffen(e.target.checked)} />
                nur offene
              </label>

              <button
                onClick={() => api.fremdBerichtPdf(
                  projekt.id, standId || undefined)}
                style={{ marginLeft: 'auto' }}
                title="Der Bericht als PDF — die Folgen zuerst, die Änderungen
                       als Nachweis dahinter"
              >
                Bericht als PDF
              </button>
            </div>

            {/* Die Bezugsstände getrennt genannt: Sie haben keine Änderungen,
                und ohne diesen Satz sähe das nach einem Fehler aus. */}
            {staende!.some((s) => s.istBezugsstand) && (
              <p className="schwach" style={{ fontSize: 12, margin: '10px 0 0' }}>
                {staende!.filter((s) => s.istBezugsstand).length} Bezugsstand
                (Erstaufnahme) — er hat keinen Vergleich und steht deshalb
                nicht in der Auswahl.
              </p>
            )}
          </div>

          <div className="reiter" role="tablist" style={{ margin: '12px 0' }}>
            <button
              type="button" role="tab"
              aria-selected={ansicht === 'aenderungen'}
              className={`reiter-knopf${ansicht === 'aenderungen' ? ' aktiv' : ''}`}
              onClick={() => setAnsicht('aenderungen')}
            >
              Änderungen ({z(baum?.gesamt ?? 0)})
            </button>
            <button
              type="button" role="tab"
              aria-selected={ansicht === 'folgen'}
              className={`reiter-knopf${ansicht === 'folgen' ? ' aktiv' : ''}`}
              onClick={() => setAnsicht('folgen')}
            >
              Folgen im eigenen Bestand ({z(folgen?.gesamt ?? 0)})
            </button>
          </div>

          {ansicht === 'folgen' && (
            <Folgenansicht
              api={api} folgen={folgen} pruefend={pruefend}
              pruefmeldung={pruefmeldung}
              pruefen={folgenPruefen} nachAenderung={folgenLaden}
            />
          )}

          {ansicht === 'aenderungen' && (
            <>
              {!baum && <LoadingState text="Änderungen werden geladen …" zeilen={8} />}

              {baum && baum.gesamt === 0 && (
                <div className="karte">
                  <div className="leer">
                    <IconOk size={18} /> Keine {nurOffen ? 'offene ' : ''}
                    Änderung in diesem Stand.
                  </div>
                </div>
              )}

              {baum && baum.gesamt > 0 && (
                <div className="karte"
                     style={{ display: 'flex', gap: 0, padding: 0 }}>
                  {/* Links Geschoss → Planungsbereich */}
                  <div className="baum">
                    <div style={{ padding: '10px 12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center',
                                      gap: 6 }}>
                        <IconSuche size={14} />
                        <input
                          value={suche}
                          onChange={(e) => setSuche(e.target.value)}
                          placeholder="Bereich, Typ oder Kategorie"
                          style={{ width: '100%', padding: '4px 6px',
                                   fontSize: 13 }}
                        />
                      </label>
                    </div>

                    {(gefiltert ?? []).map((g: any) => (
                      <div key={g.geschoss}>
                        <button
                          className="baum-ebene"
                          onClick={() => setOffen((v) => {
                            const n = new Set(v);
                            if (n.has(g.geschoss)) n.delete(g.geschoss);
                            else n.add(g.geschoss);
                            return n;
                          })}
                        >
                          <IconEbene size={14} />
                          <span>{g.geschoss}</span>

                          <span style={{ display: 'flex', gap: 5,
                                         marginLeft: 'auto', fontSize: 10,
                                         alignItems: 'center' }}>
                            {g.kritisch > 0 && (
                              <span style={{ color: 'var(--schlecht)' }}
                                    title="kritisch">{z(g.kritisch)}!</span>
                            )}
                            {g.offen > 0 && (
                              <span style={{ color: 'var(--warnung)' }}
                                    title="offen">{z(g.offen)}</span>
                            )}
                            <span className="schwach">{z(g.anzahl)}</span>
                          </span>
                        </button>

                        {offen.has(g.geschoss) && (g.bereiche ?? []).map(
                          (b: any) => (
                          <button
                            key={b.planungsbereich}
                            className={`baum-zeile ${
                              gewaehlt?.geschoss === g.geschoss
                              && gewaehlt?.bereich === b.planungsbereich
                                ? 'aktiv' : ''}`}
                            onClick={() => setGewaehlt({
                              geschoss: g.geschoss,
                              bereich: b.planungsbereich,
                            })}
                            title={b.planungsbereich}
                          >
                            <span style={{ minWidth: 0, overflow: 'hidden',
                                           textOverflow: 'ellipsis',
                                           whiteSpace: 'nowrap' }}>
                              {b.planungsbereich}
                            </span>
                            <span className="schwach">{z(b.anzahl)}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Rechts die Änderungen des gewählten Bereichs */}
                  <div style={{ flex: 1, padding: '14px 16px', minWidth: 0,
                                maxHeight: '70vh', overflowY: 'auto' }}>
                    {!gewaehlterBereich ? (
                      <div className="leer">
                        Links einen Planungsbereich wählen.
                      </div>
                    ) : (
                      <Aenderungsliste
                        api={api}
                        geschoss={gewaehlt!.geschoss}
                        bereich={gewaehlterBereich}
                        nachAenderung={laden}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

function z(x: number) { return (x ?? 0).toLocaleString('de-DE'); }

/**
 * Die Änderungen eines Planungsbereichs — nach Relevanz sortiert.
 *
 * Vorher stand die Liste in Modellreihenfolge. Eine kritische Änderung auf
 * Zeile 340 ist praktisch unsichtbar, und genau sie ist der Grund, warum
 * jemand die Seite öffnet.
 */
function Aenderungsliste({ api, geschoss, bereich, nachAenderung }: {
  api: ApiClient; geschoss: string; bereich: any; nachAenderung: () => void;
}) {
  const sortiert = useMemo(() => [...(bereich.aenderungen ?? [])].sort(
    (a, b) => {
      const ra = RELEVANZ[a.relevanz]?.rang ?? 9;
      const rb = RELEVANZ[b.relevanz]?.rang ?? 9;
      if (ra !== rb) return ra - rb;

      // Offene vor erledigten: Was abgehakt ist, muss nicht oben stehen.
      const oa = a.zustand === 'erledigt' ? 1 : 0;
      const ob = b.zustand === 'erledigt' ? 1 : 0;
      if (oa !== ob) return oa - ob;

      return (b.betragMm ?? 0) - (a.betragMm ?? 0);
    }), [bereich]);

  const spalten: Spalte<any>[] = [
    {
      schluessel: 'relevanz', titel: 'Relevanz', art: 'status', breite: '130px',
      wert: (a2) => RELEVANZ[a2.relevanz]?.rang ?? 9,
      inhalt: (a2) => (
        <StatusBadge
          klein
          zustand={RELEVANZ[a2.relevanz]?.zustand ?? 'neutral'}
          text={RELEVANZ[a2.relevanz]?.text ?? a2.relevanz}
        />
      ),
    },
    {
      schluessel: 'bauteil', titel: 'Bauteil', immerSichtbar: true,
      wert: (a2) => a2.typname,
      inhalt: (a2) => (
        <>
          {a2.typname}
          <div className="schwach" style={{ fontSize: 11 }}>
            {a2.kategorie?.replace('OST_', '')}
          </div>
        </>
      ),
    },
    {
      schluessel: 'art', titel: 'Art', breite: '150px',
      wert: (a2) => ART[a2.art] ?? a2.art,
    },
    {
      schluessel: 'merkmal', titel: 'Merkmal',
      wert: (a2) => a2.merkmal ?? '',
      inhalt: (a2) => a2.merkmal ?? '—',
    },
    {
      schluessel: 'betrag', titel: 'Betrag', art: 'zahl', einheit: 'mm',
      nachkomma: 0, breite: '110px',
      wert: (a2) => a2.betragMm,
    },
    {
      /* Wer nacharbeiten muss — aus der Folgeregel. „offen" heißt: keine Regel
         greift. Das ist eine Auskunft und kein fehlender Wert, und sie gehört
         sichtbar: Diese Änderungen sind die, für die noch niemand zuständig
         ist. */
      schluessel: 'gewerk', titel: 'Gewerk', art: 'status', breite: '130px',
      wert: (a2) => a2.gewerke ?? '',
      inhalt: (a2) => {
        if (a2.gewerke === 'offen') {
          return <StatusBadge zustand="warnung" text="offen" klein
                              titel="Keine Folgeregel greift" />;
        }
        if (a2.gewerke === 'alle') return <span className="schwach">alle</span>;
        return (a2.gewerke ?? '—').toUpperCase();
      },
    },
    {
      schluessel: 'anweisung', titel: 'Anweisung',
      wert: (a2) => a2.anweisung ?? '',
      inhalt: (a2) => <span style={{ fontSize: 12 }}>{a2.anweisung ?? '—'}</span>,
    },
    {
      schluessel: 'prioritaet', titel: 'Priorität', breite: '120px',
      wert: (a2) => a2.prioritaet ?? '',
      inhalt: (a2) => (
        <select
          value={a2.prioritaet ?? ''}
          aria-label={`Priorität für ${a2.typname}`}
          onClick={(e) => e.stopPropagation()}
          onChange={async (e) => {
            await api.fremdPrioritaet(a2.id, e.target.value || null);
            nachAenderung();
          }}
          style={{ fontSize: 11 }}
        >
          <option value="">—</option>
          <option value="hoch">hoch</option>
          <option value="normal">normal</option>
        </select>
      ),
    },
    {
      schluessel: 'zustand', titel: '', breite: '140px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (a2) => (
        <button
          className="m-knopf"
          onClick={async (e) => {
            e.stopPropagation();
            await api.fremdErledigt(a2.id, a2.zustand !== 'erledigt');
            nachAenderung();
          }}
        >
          {a2.zustand === 'erledigt' ? 'zurückstellen' : 'erledigt'}
        </button>
      ),
    },
  ];

  return (
    <DataTable
      name={`${geschoss} · ${bereich.planungsbereich}`}
      beschreibung={`${z(bereich.anzahl)} Änderungen · nach Relevanz sortiert`}
      spalten={spalten}
      zeilen={sortiert}
      schluessel={(a2) => a2.id}
      ausfuhr
      ausfuhrDateiname={`Aenderungen-${geschoss}`}
      leer={(
        <EmptyState
          titel="Keine Änderung in diesem Bereich."
          erklaerung="Der Vorwochenstand und der aktuelle Stand sind hier gleich."
        />
      )}
    />
  );
}

/**
 * Die Folgen — der eigentliche Wert des Moduls, und deshalb eine eigene
 * Ansicht.
 *
 * Die Änderungsliste sagt: „Wand W-042 hat den Typ gewechselt." Die
 * Folgenprüfung sagt: „Die zwölf Brandschotts in dieser Wand sind für
 * Trockenbau zugelassen. Die Zulassung gilt nicht mehr."
 *
 * Für den Architekten ist der Typwechsel eine Zeile. Für die TGA sind es
 * zwölf Produkte mit neuen Zulassungsnummern — und das findet von Hand
 * niemand.
 */
function Folgenansicht({ api, folgen, pruefend, pruefmeldung, pruefen,
                         nachAenderung }: {
  api: ApiClient; folgen: any; pruefend: boolean; pruefmeldung: string | null;
  pruefen: () => void; nachAenderung: () => void;
}) {
  const sortiert = useMemo(() => [...(folgen?.folgen ?? [])].sort((a, b) => {
    const ra = RELEVANZ[a.relevanz]?.rang ?? 9;
    const rb = RELEVANZ[b.relevanz]?.rang ?? 9;
    if (ra !== rb) return ra - rb;

    const oa = a.zustand === 'offen' ? 0 : 1;
    const ob = b.zustand === 'offen' ? 0 : 1;
    if (oa !== ob) return oa - ob;

    return (b.anzahl ?? 0) - (a.anzahl ?? 0);
  }), [folgen]);

  const spalten: Spalte<any>[] = [
    {
      schluessel: 'relevanz', titel: 'Relevanz', art: 'status', breite: '130px',
      wert: (f) => RELEVANZ[f.relevanz]?.rang ?? 9,
      inhalt: (f) => (
        <StatusBadge
          klein
          zustand={RELEVANZ[f.relevanz]?.zustand ?? 'neutral'}
          text={RELEVANZ[f.relevanz]?.text ?? f.relevanz}
        />
      ),
    },
    {
      schluessel: 'betroffen', titel: 'Betrifft', immerSichtbar: true,
      wert: (f) => f.betroffen,
    },
    {
      schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', breite: '100px',
      wert: (f) => f.anzahl,
    },
    {
      schluessel: 'pruefung', titel: 'Prüfung',
      wert: (f) => f.pruefung,
      inhalt: (f) => (
        <span style={{ fontSize: 12 }}>
          {f.pruefung}
          {f.kennzeichen && (
            <div className="schwach" style={{ fontSize: 11 }}>{f.kennzeichen}</div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'ort', titel: 'Ort', breite: '170px',
      wert: (f) => `${f.geschoss ?? ''} ${f.planungsbereich ?? ''}`.trim(),
      inhalt: (f) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {f.geschoss} {f.planungsbereich}
        </span>
      ),
    },
    {
      schluessel: 'zustand', titel: '', breite: '140px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (f) => (
        <button className="m-knopf"
                onClick={async (e) => {
                  e.stopPropagation();
                  await api.fremdFolgeZustand(
                    f.id, f.zustand === 'offen' ? 'erledigt' : 'offen');
                  nachAenderung();
                }}>
          {f.zustand === 'offen' ? 'erledigt' : 'zurückstellen'}
        </button>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Folgen im eigenen Bestand"
      beschreibung={'Die Änderungsliste sagt: „Wand W-042 hat den Typ gewechselt." Die '
        + 'Folgenprüfung sagt: „Die zwölf Brandschotts in dieser Wand sind für '
        + 'Trockenbau zugelassen. Die Zulassung gilt nicht mehr."'}
      meta={folgen && folgen.gesamt > 0
        ? `${z(folgen.gesamt)} Folgen · ${z(folgen.betroffeneBauteile)} betroffene Bauteile`
        : undefined}
      aktionen={(
        <button onClick={pruefen} disabled={pruefend} className="m-knopf m-knopf-haupt">
          {pruefend ? 'wird geprüft …' : 'Folgen prüfen'}
        </button>
      )}
      nackt
    >
      {pruefmeldung && <div className="hinweis">{pruefmeldung}</div>}

      <DataTable
        spalten={spalten}
        zeilen={sortiert}
        schluessel={(f) => f.id}
        ausfuhr
        ausfuhrDateiname="Folgen"
        leer={(
          <EmptyState
            titel="Noch keine Folgen geprüft — oder keine gefunden."
            erklaerung={'Geprüft wird die Kette Bauteil → Aussparung → Brandschott, und '
              + 'zwar exakt über die Bauteilkennung: Eine Wand, die sich verschiebt, '
              + 'betrifft genau die Aussparungen, die in ihr sitzen. Folgen über einen '
              + 'Umkreis brauchen die Lage der eigenen Bauteile — die liefert erst die '
              + 'Koordinationsaufnahme.'}
            aktion={(
              <button className="m-knopf m-knopf-haupt" onClick={pruefen}
                      disabled={pruefend}>
                {pruefend ? 'wird geprüft …' : 'Folgen prüfen'}
              </button>
            )}
          />
        )}
      />
    </ContentSection>
  );
}
